import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPen,
  faCheck,
  faTimes,
  faHotel,
  faCalendarDay,
} from "@fortawesome/free-solid-svg-icons";
import { utcToLADateKey, formatLADateKeyDDMMMMYYYY } from "../../utils/laDateUtils";
import API from "../../api/axiosInstance";
import AllocationModal from "./AllocationModal";
import BookingCellMenu from "./BookingCellMenu";
import { GoldLoader } from "../shared/Loaders";
import {
  formatHeaderDay,
  formatHeaderDate,
  cellKey,
  groupBookingsByParticipant,
} from "./roomsInformationUtils";
import {
  AvailableCell,
  DisabledCell,
  BookedCell,
} from "./RoomsInformationCells";
import { useRoomsInformationData } from "./useRoomsInformationData";
import { totalCapacity } from "../../constants/roomConfig";
import "../css/RoomsInformation.css";

const RoomsInformation = () => {
  const [searchParams] = useSearchParams();
  const hotelIdFromUrl = searchParams.get("hotel_id");
  const roomIdFromUrl = searchParams.get("room_id");
  const dateFromUrl = searchParams.get("date");

  const data = useRoomsInformationData(hotelIdFromUrl, roomIdFromUrl, dateFromUrl);
  const {
    hotels,
    selectedHotel,
    setSelectedHotel,
    rooms,
    selectedRoom,
    setSelectedRoom,
    roomUnits,
    setRoomUnits,
    dates,
    bookingsMap,
    defaultPrice,
    bedCount,
    isOutOfRange,
    isPastDate,
    todayLA,
    shiftDates,
    goToDate,
    getCachedWeek,
    fetchBookings,
    loading,
    loadingHotels,
    loadingRooms,
    loadingUnits,
  } = data;

  const [allocModal, setAllocModal] = useState(null);
  const [cellMenu, setCellMenu] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [transition, setTransition] = useState(null);
  const transitionCounterRef = useRef(0);

  const isDragSelected = useCallback(
    (unitId, colIdx) => {
      if (!dragState || dragState.mode !== "create" || dragState.unitId !== unitId)
        return false;
      const lo = Math.min(dragState.startColIdx, dragState.currentColIdx);
      const hi = Math.max(dragState.startColIdx, dragState.currentColIdx);
      return colIdx >= lo && colIdx <= hi;
    },
    [dragState]
  );

  const isEditDragSelected = useCallback(
    (unitId, colIdx) => {
      if (!dragState || dragState.mode !== "edit" || dragState.unitId !== unitId)
        return false;
      const lo = Math.min(dragState.startColIdx, dragState.currentColIdx);
      const hi = Math.max(dragState.startColIdx, dragState.currentColIdx);
      return colIdx >= lo && colIdx <= hi;
    },
    [dragState]
  );

  const finalizeDrag = useCallback(() => {
    setDragState((prev) => {
      if (!prev) return null;
      const lo = Math.min(prev.startColIdx, prev.currentColIdx);
      const hi = Math.max(prev.startColIdx, prev.currentColIdx);

      if (prev.mode === "edit") {
        if (lo === hi) {
          const date = dates[lo];
          const k = cellKey(prev.unitId, date);
          const cb = bookingsMap[k];
          if (cb && cb.length > 0) {
            setCellMenu({ bookings: cb, unit: prev.unit, date });
          }
        } else {
          const allBookings = [];
          for (let i = lo; i <= hi; i++) {
            const k = cellKey(prev.unitId, dates[i]);
            const cb = bookingsMap[k];
            if (cb) allBookings.push(...cb);
          }
          if (allBookings.length > 0) {
            const groupedEntries = groupBookingsByParticipant(allBookings);
            setCellMenu({
              unit: prev.unit,
              date: dates[lo],
              groupedEntries,
              dateRange: {
                from: utcToLADateKey(dates[lo]),
                to: utcToLADateKey(dates[hi]),
              },
            });
          }
        }
      } else {
        const selectedDates = dates
          .slice(lo, hi + 1)
          .filter((d) => !isOutOfRange(d));
        if (selectedDates.length > 0) {
          setAllocModal({ unit: prev.unit, dates: selectedDates });
        }
      }

      return null;
    });
  }, [dates, isOutOfRange, isPastDate, bookingsMap]);

  useEffect(() => {
    const onMouseUp = () => finalizeDrag();
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [finalizeDrag]);

  const SHIFT_IN_MS = 350;

  const handleShift = (direction) => {
    if (transition) return;
    transitionCounterRef.current += 1;
    const outgoing = { dates, bookingsMap };
    const cached = getCachedWeek(direction);
    const dir = direction > 0 ? "next" : "prev";
    if (cached) {
      setTransition({ dir, outgoing, incoming: cached, incomingReady: true });
    } else {
      setTransition({ dir, outgoing, incoming: outgoing, incomingReady: false });
    }
    shiftDates(direction);
  };

  useEffect(() => {
    if (!transition || transition.incomingReady || loading) return;
    setTransition((prev) =>
      prev && !prev.incomingReady
        ? { ...prev, incoming: { dates, bookingsMap }, incomingReady: true }
        : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transition, loading, dates, bookingsMap]);

  useEffect(() => {
    if (!transition || !transition.incomingReady) return;
    const t = setTimeout(() => setTransition(null), SHIFT_IN_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transition?.incomingReady]);

  const handleAvailableMouseDown = (unit, colIdx, e) => {
    e.preventDefault();
    setDragState({
      unitId: unit._id,
      unit,
      startColIdx: colIdx,
      currentColIdx: colIdx,
      mode: "create",
    });
  };

  const handleBookedMouseDown = (unit, colIdx, e) => {
    if (e.target.closest("button")) return;
    e.preventDefault();
    setDragState({
      unitId: unit._id,
      unit,
      startColIdx: colIdx,
      currentColIdx: colIdx,
      mode: "edit",
    });
  };

  const handleCellMouseEnter = (unitId, colIdx, isBooked) => {
    setDragState((prev) => {
      if (!prev || prev.unitId !== unitId) return prev;
      if (prev.mode === "edit" && !isBooked) return prev;
      if (prev.mode === "create" && isBooked) return prev;
      if (prev.mode === "create" && !isBooked) {
        const lo = Math.min(prev.currentColIdx, colIdx);
        const hi = Math.max(prev.currentColIdx, colIdx);
        for (let i = lo; i <= hi; i++) {
          const k = cellKey(unitId, dates[i]);
          const cb = bookingsMap[k];
          if (cb && cb.length > 0) return prev;
        }
      }
      return { ...prev, currentColIdx: colIdx };
    });
  };

  const handleBookingCreated = () => {
    setAllocModal(null);
    fetchBookings();
  };

  const handleBookingChanged = (message) => {
    setCellMenu(null);
    fetchBookings();
    if (message) {
      setToastMessage(message);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3500);
    }
  };

  const handleAddAnother = () => {
    if (!cellMenu) return;
    const { unit, date, dateRange } = cellMenu;
    setCellMenu(null);
    if (dateRange) {
      const selectedDates = dates.filter((d) => {
        const key = utcToLADateKey(d);
        return key >= dateRange.from && key <= dateRange.to && !isOutOfRange(d);
      });
      setAllocModal({ unit, dates: selectedDates.length > 0 ? selectedDates : [date] });
    } else {
      setAllocModal({ unit, dates: [date] });
    }
  };

  const handleLabelSave = async (unitId) => {
    if (!editingLabel || editingLabel.value.trim() === "") return;
    try {
      await API.put(`/ems/room-units/${unitId}`, {
        room_label: editingLabel.value.trim(),
      });
      setRoomUnits((prev) =>
        prev.map((u) =>
          u._id === unitId ? { ...u, room_label: editingLabel.value.trim() } : u
        )
      );
      setEditingLabel(null);
    } catch (err) {
      console.error("Failed to update label:", err);
    }
  };

  const handleInitializeRoomUnits = async () => {
    if (!selectedRoom) return;
    setInitializing(true);
    try {
      const res = await API.post("/ems/room-units/initialize", {
        room_id: selectedRoom,
      });
      const units = res.data?.units || [];
      setRoomUnits(Array.isArray(units) ? units : []);
    } catch (err) {
      console.error("Initialize room units failed:", err);
      alert(err.response?.data?.message || "Failed to initialize room units");
    } finally {
      setInitializing(false);
    }
  };

  const unitCapacity = (unit) =>
    unit.configuration_id ? totalCapacity(unit.configuration_id.beds) : (unit.room_id?.bed_count || bedCount);

  const unitPrice = (unit) =>
    unit.configuration_id?.price_per_night ?? defaultPrice;

  const renderDateGridTable = ({
    dates: gridDates,
    bookingsMap: gridBookingsMap,
    interactive,
    overlay,
    animClass,
    tableKey,
  }) => (
    <table
      key={tableKey}
      className={`rooms-info-table rooms-info-date-grid-table${
        overlay ? " rooms-info-date-grid-slide--overlay" : ""
      }${animClass ? ` ${animClass}` : ""}`}
    >
      <thead>
        <tr>
          {gridDates.map((date, i) => {
            const isToday = utcToLADateKey(date) === todayLA;
            return (
              <th key={i} className="rooms-info-date-th">
                <div
                  className={`rooms-info-date-card${isToday ? " rooms-info-date-card--today" : ""}`}
                >
                  <span className="rooms-info-date-day">
                    {formatHeaderDay(date)}
                    {isToday && <span className="rooms-info-date-today-badge">Today</span>}
                  </span>
                  <span className="rooms-info-date-label">
                    {formatHeaderDate(date)}
                  </span>
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {roomUnits.map((unit) => (
          <tr key={unit._id}>
            {gridDates.map((date, colIdx) => {
              if (isOutOfRange(date)) {
                return (
                  <td
                    key={colIdx}
                    onMouseEnter={
                      interactive
                        ? () => handleCellMouseEnter(unit._id, colIdx, false)
                        : undefined
                    }
                  >
                    <DisabledCell />
                  </td>
                );
              }

              const k = cellKey(unit._id, date);
              const cellBookings = gridBookingsMap[k];
              const price = `$${unitPrice(unit)}`;
              const past = isPastDate(date);

              if (cellBookings && cellBookings.length > 0) {
                return (
                  <td
                    key={colIdx}
                    onMouseDown={
                      interactive
                        ? (e) => handleBookedMouseDown(unit, colIdx, e)
                        : undefined
                    }
                    onMouseEnter={
                      interactive
                        ? () => handleCellMouseEnter(unit._id, colIdx, true)
                        : undefined
                    }
                  >
                    <BookedCell
                      cellBookings={cellBookings}
                      bedCount={unitCapacity(unit)}
                      defaultPrice={defaultPrice}
                      isPast={past}
                      editSelected={
                        interactive
                          ? isEditDragSelected(unit._id, colIdx)
                          : false
                      }
                    />
                  </td>
                );
              }

              return (
                <td
                  key={colIdx}
                  onMouseEnter={
                    interactive
                      ? () => handleCellMouseEnter(unit._id, colIdx, false)
                      : undefined
                  }
                >
                  <AvailableCell
                    price={price}
                    configName={unit.configuration_id?.name}
                    selected={interactive ? isDragSelected(unit._id, colIdx) : false}
                    isPast={past}
                    onMouseDown={
                      interactive
                        ? (e) => handleAvailableMouseDown(unit, colIdx, e)
                        : undefined
                    }
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const pageLoading =
    loadingHotels ||
    (selectedHotel && loadingRooms) ||
    (selectedRoom && loadingUnits);

  if (pageLoading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="rooms-info-page">
      {showToast && (
        <div className="copy-toast">
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: "10px", color: "#D9A128" }} />
          {toastMessage}
        </div>
      )}
      <div className="rooms-info-topbar">
        <h2 className="rooms-info-title">Rooms information</h2>
        <div className="rooms-info-selects">
          <div className="rooms-info-select-group">
            <label className="rooms-info-select-label" htmlFor="rooms-info-hotel">
              Hotel
            </label>
            <select
              id="rooms-info-hotel"
              className="rooms-info-hotel-select"
              value={selectedHotel}
              onChange={(e) => setSelectedHotel(e.target.value)}
            >
              <option value="" disabled={!!selectedHotel} hidden={!!selectedHotel}>
                Select Hotel
              </option>
              {hotels.map((h) => (
                <option key={h._id} value={h._id}>
                  {h.global_hotel_id?.global_hotel_name || "Hotel"}
                </option>
              ))}
            </select>
          </div>
          {rooms.length > 0 && (
            <div className="rooms-info-select-group">
              <label
                className="rooms-info-select-label"
                htmlFor="rooms-info-room-type"
              >
                Room Name
              </label>
              <select
                id="rooms-info-room-type"
                className="rooms-info-hotel-select"
                value={selectedRoom}
                onChange={(e) => setSelectedRoom(e.target.value)}
              >
                {rooms.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.room_name || r.room_type}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedRoom && (
            <div className="rooms-info-select-group">
              <label
                className="rooms-info-select-label"
                htmlFor="rooms-info-date-jump"
              >
                Jump to date
              </label>
              <div className="rooms-info-date-jump">
                <FontAwesomeIcon
                  icon={faCalendarDay}
                  className="rooms-info-date-jump__icon"
                />
                <span className="rooms-info-date-jump__display">
                  {formatLADateKeyDDMMMMYYYY(utcToLADateKey(dates[0]))}
                </span>
                <input
                  id="rooms-info-date-jump"
                  type="date"
                  className="rooms-info-date-jump__input"
                  value={utcToLADateKey(dates[0])}
                  onChange={(e) => goToDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {!selectedHotel ? (
        <div className="rooms-info-empty-state">
          <div className="rooms-info-empty-state-icon">
            <FontAwesomeIcon icon={faHotel} />
          </div>
          <p className="rooms-info-empty-state-text">
            Select a hotel to view its room calendar
          </p>
          <p className="rooms-info-empty-state-subtext">
            Choose a hotel from the dropdown above to get started.
          </p>
        </div>
      ) : roomUnits.length === 0 && selectedRoom ? (
        <div style={{ color: "#555", padding: "20px", maxWidth: "480px" }}>
          <p style={{ marginBottom: "12px" }}>
            No room units found for this room type. Units are created when rooms
            are added or capacity is increased.
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#888",
              marginBottom: "16px",
            }}
          >
            If this room type was created before the booking feature, you can
            create units from its current capacity.
          </p>
          <button
            className="btn btn-warning"
            onClick={handleInitializeRoomUnits}
            disabled={initializing}
          >
            {initializing ? "Initializing…" : "Initialize room units"}
          </button>
        </div>
      ) : (
        <div className="rooms-info-grid-wrapper">
          {loading && (
            <div className="rooms-info-loading-badge">
              <GoldLoader label="Loading..." variant="badge" />
            </div>
          )}
          <div className="rooms-info-grid-split">
            <div className="rooms-info-room-sidebar">
              <div className="rooms-info-room-header">
                <div className="rooms-info-nav-buttons">
                  <button
                    className="rooms-info-nav-btn"
                    onClick={() => handleShift(-1)}
                    title="Previous week"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    className="rooms-info-nav-btn"
                    onClick={() => handleShift(1)}
                    title="Next week"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
                <span className="rooms-info-room-header-label">Room</span>
              </div>
              {roomUnits.map((unit) => (
                <div className="rooms-info-room-card" key={unit._id}>
                  {editingLabel?.unitId === unit._id ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <input
                        style={{
                          width: "60px",
                          fontSize: "14px",
                          padding: "2px 4px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                        value={editingLabel.value}
                        onChange={(e) =>
                          setEditingLabel({
                            ...editingLabel,
                            value: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleLabelSave(unit._id);
                          if (e.key === "Escape") setEditingLabel(null);
                        }}
                        autoFocus
                      />
                      <FontAwesomeIcon
                        icon={faCheck}
                        style={{
                          cursor: "pointer",
                          color: "#28a745",
                          fontSize: "12px",
                        }}
                        onClick={() => handleLabelSave(unit._id)}
                      />
                      <FontAwesomeIcon
                        icon={faTimes}
                        style={{
                          cursor: "pointer",
                          color: "#dc3545",
                          fontSize: "12px",
                        }}
                        onClick={() => setEditingLabel(null)}
                      />
                    </div>
                  ) : (
                    <span
                      className="rooms-info-room-number"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {unit.room_label}
                      <FontAwesomeIcon
                        icon={faPen}
                        className="rooms-info-room-edit-icon"
                        title="Edit room label"
                        onClick={() =>
                          setEditingLabel({
                            unitId: unit._id,
                            value: unit.room_label,
                          })
                        }
                      />
                    </span>
                  )}
                  <span className="rooms-info-room-beds">
                    <img
                      src="/images/bed-icon.png"
                      alt=""
                      className="rooms-info-bed-icon"
                    />
                    Beds: {unitCapacity(unit)}
                  </span>
                  {unit.configuration_id?.name && (
                    <span className="rooms-info-room-config-name">
                      {unit.configuration_id.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="rooms-info-date-grid-pane">
              <div className="rooms-info-date-grid-viewport">
                {!transition &&
                  renderDateGridTable({
                    dates,
                    bookingsMap,
                    interactive: true,
                    overlay: false,
                    tableKey: "steady",
                  })}
                {transition && !transition.incomingReady && (
                  renderDateGridTable({
                    dates: transition.outgoing.dates,
                    bookingsMap: transition.outgoing.bookingsMap,
                    interactive: false,
                    overlay: false,
                    tableKey: "outgoing-static",
                  })
                )}
                {transition && transition.incomingReady && (
                  <React.Fragment>
                    {renderDateGridTable({
                      dates: transition.outgoing.dates,
                      bookingsMap: transition.outgoing.bookingsMap,
                      interactive: false,
                      overlay: true,
                      animClass:
                        transition.dir === "next"
                          ? "rooms-info-date-grid-slide-out-left"
                          : "rooms-info-date-grid-slide-out-right",
                      tableKey: `outgoing-${transitionCounterRef.current}`,
                    })}
                    {renderDateGridTable({
                      dates: transition.incoming.dates,
                      bookingsMap: transition.incoming.bookingsMap,
                      interactive: true,
                      overlay: false,
                      animClass:
                        transition.dir === "next"
                          ? "rooms-info-date-grid-slide-in-right"
                          : "rooms-info-date-grid-slide-in-left",
                      tableKey: `incoming-${transitionCounterRef.current}`,
                    })}
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {allocModal && !editEntry && (
        <AllocationModal
          unit={allocModal.unit}
          dates={allocModal.dates}
          bedCount={unitCapacity(allocModal.unit)}
          defaultPrice={defaultPrice}
          onClose={() => setAllocModal(null)}
          onCreated={handleBookingCreated}
          hotelName={hotels.find((h) => h._id === selectedHotel)?.global_hotel_id?.global_hotel_name || ""}
          roomType={rooms.find((r) => r._id === selectedRoom)?.room_name || ""}
          competitionId={hotels.find((h) => h._id === selectedHotel)?.competition_id?._id || hotels.find((h) => h._id === selectedHotel)?.competition_id || ""}
        />
      )}

      {editEntry && cellMenu && (
        <AllocationModal
          unit={cellMenu.unit}
          dates={[]}
          bedCount={unitCapacity(cellMenu.unit)}
          defaultPrice={defaultPrice}
          editEntry={editEntry}
          onClose={() => setEditEntry(null)}
          onCreated={() => {}}
          onUpdated={() => {
            setEditEntry(null);
            setCellMenu(null);
            fetchBookings();
          }}
          hotelName={hotels.find((h) => h._id === selectedHotel)?.global_hotel_id?.global_hotel_name || ""}
          roomType={rooms.find((r) => r._id === selectedRoom)?.room_name || ""}
          competitionId={hotels.find((h) => h._id === selectedHotel)?.competition_id?._id || hotels.find((h) => h._id === selectedHotel)?.competition_id || ""}
        />
      )}

      {cellMenu && !editEntry && (
        <BookingCellMenu
          bookings={cellMenu.bookings}
          unit={cellMenu.unit}
          date={cellMenu.date}
          bedCount={unitCapacity(cellMenu.unit)}
          defaultPrice={defaultPrice}
          isPast={isPastDate(cellMenu.date)}
          onClose={() => setCellMenu(null)}
          onChanged={handleBookingChanged}
          onRefresh={fetchBookings}
          onAddAnother={handleAddAnother}
          groupedEntries={cellMenu.groupedEntries}
          dateRange={cellMenu.dateRange}
          onEditEntry={(entry) => setEditEntry(entry)}
          hotelName={hotels.find((h) => h._id === selectedHotel)?.global_hotel_id?.global_hotel_name || ""}
          roomType={rooms.find((r) => r._id === selectedRoom)?.room_name || ""}
          competitionId={hotels.find((h) => h._id === selectedHotel)?.competition_id?._id || hotels.find((h) => h._id === selectedHotel)?.competition_id || ""}
        />
      )}
    </div>
  );
};

export default RoomsInformation;
